// ES Module version for React app
// Duplicated logic to avoid CommonJS/ESM interop issues

// Legacy locations: Only require 'l' and 'sc' parameters
const LEGACY_REDIRECT_CONFIG = {
  country: "US",
  locations: [
    "5179",
    "7045",
    "34",
    "150003",
    "155468",
    "170063",
    "142201",
    "169960",
    "155116",
    "103033",
    "142868",
    "160488",
    "62615",
    "199186",
    "199188",
    "199189",
    "199190",
    "199192",
    "199193",
  ],
  requiredParams: {
    sc: ["t2p", "pnqr"], // sc must be one of these values
  },
};

// New locations: Require 'l', 's=ttp' (no longer required), and 'sc' parameters
const NEW_REDIRECT_CONFIG = {
  country: "US",
  locations: ["300", "74455", "195313", "111185", "296", "206201", "149306"],
  requiredParams: {
    // s: ["ttp"], // s must equal "ttp"
    sc: ["t2p", "pnqr"], // sc must be one of these values
  },
};

// Widget entry: the Atlanta Braves and BJCC legacy Simple Widgets hand their
// Get Rate / Buy Now click off to laz.io with the full widget context
// (evid, t, wt, isocode, wk, start, end, rate ids, attribution, ...) preserved.
//
// The widget key is the client identifier and the only selector. Location IDs
// are deliberately not used to gate this: each embed offers a long
// data-locationid list (5 for Braves, 26 for BJCC) that the client can change
// without telling us, and a stale list would silently drop traffic back to the
// legacy checkout - the exact chargeback exposure this handoff exists to close.
const WIDGET_REDIRECT_CONFIG = {
  country: "US", // every allowlisted key is a US client - see CA_REDIRECT_CONFIG
  requiredParams: {
    wk: [
      "8348904147504e4f9cfa7bf09d3c94d6", // Atlanta Braves
      "4b195fd960d348d785ba20b82839dba0", // Atlanta Braves
      "ca7d2fc559b04036bcb2894570bd2a34", // BJCC (website)
      "38f47f9890854872a929948d76786a51", // BJCC (Coca-Cola Amp)
      "436080f3b7294ef796f7081d3cc84bad", // BJCC (Stallions)
      "c5ea4ed0b654441f93b8e5db4cc52dc4", // BJCC (Broadway)
      "37f12b30976244a4b79c0a327a403bc9", // BJCC (Protective Stadium)
    ],
  },
  paths: ["/buynow"], // does not apply to /ebuynow
  preserveQuery: true, // forward every param except 'l'
};

// Locations that have moved to the Canadian new app. Country-scoped, and the
// scoping is the point: the CA legacy app talks to a SEPARATE GRS dataset from
// the US one (config/.env.prd-ca points at GRSv2API-CA-*, *.thx.lazparking.ca),
// so a US location id reached on the CA host resolves to nothing there. Without
// this column go.lazparking.ca/buynow?l=5179 - a Canadian page showing nothing -
// 301s to an Atlanta garage the user never asked for. Every id in the two
// allowlists above was checked against the CA dataset and came back empty, so
// scoping them US removes that leak rather than breaking anything.
//
// Every rule above carries country: "US" for the same reason, the widget rule
// included: all seven widget keys are US clients, the embeds hand off to
// go.lazparking.com, and a Braves link arriving on the Canadian host is the
// same leak wearing a different hat. US widget traffic is on the US host and is
// untouched, so the chargeback exposure that rule exists to close is not
// reopened by scoping it.
//
// sc is OPTIONAL here, unlike the US sales-channel rules: the ticket requires a
// bare /buynow?l=175448 to redirect, because the printed links and QR codes
// predate any sales-channel tagging. When sc IS present it must still be a
// recognized channel, or the request falls through rather than forwarding
// unvalidated attribution to the new app.
export const CA_REDIRECT_CONFIG = {
  country: "CA",
  locations: ["175448"], // YEG - Arrivals Paid Lot, Leduc AB (Edmonton Intl)
  paths: ["/buynow"], // does not apply to /ebuynow
  optionalParams: {
    sc: ["t2p", "pnqr"], // when present, sc must be one of these values
  },
  requiredParams: {},
};

// Kill switch for the Braves/BJCC widget handoff. It shipped ahead of schedule;
// set this to false and redeploy to disable the widget redirect. While it is
// false, normal traffic skips the widget rule, but a single request can still
// opt back in with ?rd=true (see shouldRedirect) so we can validate the handoff
// in production. This gates ONLY the widget rule - the sc-based legacy/new
// redirects are untouched.
export const WIDGET_REDIRECT_ENABLED = true;

// Kill switch for the Canadian location handoff, modelled on
// WIDGET_REDIRECT_ENABLED above. OFF until the CA cutover is signed off. While
// it is false, normal traffic is byte-for-byte today's - same targets, same
// hosts, same rules - but a single request can still opt in with ?rd=true (see
// resolveBuynowRedirect) to validate the handoff in production.
//
// Turning this ON changes three things, and ONLY on a CA host:
//   1. the /buynow target origin becomes ca.laz.io / stg-ca.laz.io
//   2. US location ids stop matching, which closes the cross-country leak
//      described on CA_REDIRECT_CONFIG
//   3. 175448 starts matching
//
// NEW_APP_ORIGINS.US equals DOMAIN_MAP by value - there is a drift test - so a
// US host resolves to the same origin under either branch and is unaffected.
export const CA_REDIRECT_ENABLED = true;

// All redirect configurations. The widget and Canadian rules join this list
// only while their kill switches are on; when one is off, shouldRedirect re-adds
// it per request for the ?rd=true override, so disabling either leaves every
// other rule unchanged.
export const REDIRECT_CONFIGS = [
  LEGACY_REDIRECT_CONFIG,
  NEW_REDIRECT_CONFIG,
  ...(WIDGET_REDIRECT_ENABLED ? [WIDGET_REDIRECT_CONFIG] : []),
  ...(CA_REDIRECT_ENABLED ? [CA_REDIRECT_CONFIG] : []),
];

export const DOMAIN_MAP = {
  dev: "https://stg.laz.io",
  uat: "https://stg.laz.io",
  staging: "https://stg.laz.io",
  prod: "https://laz.io",
};

export const TARGET_PATH = "/p";

export const AIRPORT_TARGET_PATH = "/a";

// New-app origins, country -> environment. Only "prod" and "staging" appear
// because those are the only environments resolveHost returns.
//
// DOMAIN_MAP is deliberately NOT nested by country instead: Routes.js reads it
// as domainMap.prod / domainMap[env], and a country level would make both
// undefined, which buildRedirectUrl turns into the RELATIVE path
// "undefined/p/5179" - a silent failure with no test covering it. The US column
// duplicates DOMAIN_MAP by value and a drift test asserts the two agree.
export const NEW_APP_ORIGINS = {
  US: { prod: "https://laz.io", staging: "https://stg.laz.io" },
  CA: { prod: "https://ca.laz.io", staging: "https://stg-ca.laz.io" },
};

// Airport client roots that have cut over to the new app, by deployment
// country. A SUBSET of airportClients in src/Routes.js, not a copy: a client
// joins that list the day its legacy page exists and joins this one only on
// cutover day. A drift detector in src/redirect-config.test.js asserts every id
// here appears in that list.
//
// Split by country so a US client id reached on the CA host (or the reverse)
// falls through to the legacy app instead of 301ing to an origin that has no
// such tenant. CA is empty today; flyyyj (Victoria BC) is the likely first
// entry, and ca.laz.io/a/flyyyj is already serving.
export const AIRPORT_REDIRECT_CLIENTS = {
  US: ["flycae", "flymot", "mbsairport"],
  CA: [],
};

// Locations served by the SSO purchase flow. These are denied ahead of every
// redirect rule.
//
// WIDGET_REDIRECT_CONFIG deliberately carries no `locations` allowlist, so the
// widget rule matches ANY numeric `l` paired with an allowlisted `wk`. Without
// this deny-list, laz.io -> /buynow -> redirect -> laz.io/p/<sso-id> -> SSO
// handoff -> /buynow -> ... is one config edit away, with ?rd=false the only
// escape hatch.
//
// Duplicated by value from SSO_LOCATION_IDS in
// src/components/ExternalReservationPages/ssoConfig.js - the root CommonJS twin
// of this file cannot import from src/. A drift detector in
// src/redirect-config.test.js asserts the two agree.
export const SSO_DENY_LOCATIONS = ["81354", "155882", "99960994", "97258"];

// Query keys that carry SSO credentials and must never leave this origin.
// buildLegacyPurchaseContinuation (ssoAuthHandler.js) puts token=<JWT> into a
// full-page navigation to /buynow, and ssoUtils strips these from return paths
// for exactly this reason. Once the redirect happens at the edge these would
// also sit in a Location RESPONSE HEADER - ALB access logs, intermediary
// proxies, Referer chains - so they are stripped from every forwarded query.
//
// Zero overlap with the widget params (evid t wt isocode wk start end).
// Duplicated by value from SSO_AUTH_QUERY_KEYS in ssoConfig.js; see above.
export const SSO_STRIPPED_QUERY_KEYS = [
  "access_token",
  "authorization",
  "code",
  "crm_token",
  "crmtoken",
  "error",
  "error_description",
  "id_token",
  "mfa",
  "nonce",
  "otp",
  "password",
  "refresh_token",
  "session_state",
  "state",
  "ticket",
  "token",
  "v2callback",
  "v2locationid",
];

// Host -> laz.io environment. Deliberately a strict, exhaustive, lowercased
// allowlist rather than the substring rules in Routes.js REDIRECT_CONFIG.
//
// In the browser the hostname is trusted. At the edge req.hostname is the raw
// Host header with the port stripped: attacker controlled, not lowercased, and
// undefined when the header is absent. Porting the substring rules verbatim
// gives three defects that exist only server-side:
//
//   - hostname.includes(...) on undefined THROWS, so Express would 500 /buynow
//     on HTTP/1.0 or Host-less requests that today get the SPA
//   - `Host: GO.LAZPARKING.COM` fails the === check and falls through
//   - includes("stg.") matches stg.attacker.example, and includes("uat")
//     matches any host containing those three letters - production could be
//     induced into emitting a redirect to stg.laz.io
//
// An unrecognized host resolves to null and the caller falls through to the
// SPA. That is the safe failure mode and costs nothing: the pre-render
// bootstrap and the Routes.js backstop still redirect, just without the 301.
//
// Inventory from config/.env.* MERCHANT_DOMAIN, corroborated by
// public/pass/scripts/pass.js.
// Host -> { country, environment }. Replaces the two flat host arrays that
// stood here: the same hosts, the same exact-match discipline, one more column.
//
// The country column exists because the legacy app ships as two deployments -
// US on go.lazparking.com, CA on go.lazparking.ca, driven by config/.env.prd vs
// config/.env.prd-ca - and the new app is split the same way, so a Canadian
// airport client has to land on the Canadian new app. Country comes from the
// host and nowhere else. REACT_APP_COUNTRY is disqualified for exactly the
// reason resolveTargetDomain takes no env argument: client.js never calls
// dotenv.config(), so the CommonJS copy would read undefined while the webpack
// bundled copy read "CA" on the very same request.
//
// resolveTargetDomain ignores the country column, so /buynow is unchanged.
const HOST_MAP = {
  "go.lazparking.com": { country: "US", environment: "prod" },
  "go.lazparking.ca": { country: "CA", environment: "prod" },
  "stg.grswebui.thinxsoftware.com": { country: "US", environment: "staging" }, // stg and uat
  "stg.grswebui-laz-ca.thinxsoftware.com": {
    country: "CA",
    environment: "staging",
  },
  localhost: { country: "US", environment: "staging" },
  "127.0.0.1": { country: "US", environment: "staging" },
};
// package.json startClient serves under lazparking-tech-2023.pem
// (CN=*.lazparking.tech). Without this a developer falls through to production.
// Country-neutral, so it resolves to US staging; a developer exercising the CA
// flow points at the CA staging host instead.
const LOCAL_DEVELOPMENT_HOST_SUFFIX = ".lazparking.tech";

/**
 * Resolve which deployment a request host belongs to.
 *
 * Module private on purpose. The country is an implementation detail of the
 * airport rule, and exporting this would invite a second, looser host resolver
 * alongside the one strict allowlist above.
 *
 * Guards in the order they have always run: non-string in (req.hostname is
 * undefined on HTTP/1.0 and Host-less requests) -> trim and lowercase -> empty
 * -> exact match -> the *.lazparking.tech developer suffix -> null.
 *
 * @param {string} [hostname] - req.hostname or window.location.hostname
 * @returns {{country: string, environment: string}|null}
 */
function resolveHost(hostname) {
  if (typeof hostname !== "string") return null;

  const host = hostname.trim().toLowerCase();
  if (!host) return null;

  // hasOwnProperty rather than a bare HOST_MAP[host]: `Host: constructor` and
  // `Host: __proto__` would otherwise resolve off Object.prototype.
  if (Object.prototype.hasOwnProperty.call(HOST_MAP, host)) {
    return HOST_MAP[host];
  }

  if (
    host.length > LOCAL_DEVELOPMENT_HOST_SUFFIX.length &&
    host.slice(-LOCAL_DEVELOPMENT_HOST_SUFFIX.length) ===
      LOCAL_DEVELOPMENT_HOST_SUFFIX
  ) {
    return { country: "US", environment: "staging" };
  }

  return null;
}

/**
 * Resolve the laz.io target domain from the request host.
 *
 * Takes no env argument on purpose: webpack inlines process.env at build time
 * for the React copy but not for the Node copy, so an env-derived answer would
 * let the two copies disagree. Host-only resolution cannot diverge.
 *
 * Deliberately ignores the country column: go.lazparking.ca resolves to laz.io
 * here, which is a cross-country leak and exactly what CA_REDIRECT_ENABLED
 * exists to close. This function is kept, unchanged, as the flag-OFF half of
 * resolveBuynowRedirect - it is what makes "the flag off changes nothing"
 * literally true rather than merely tested. Once the switch is permanently on,
 * this and its test block can go with the old path.
 *
 * @param {string} [hostname] - req.hostname or window.location.hostname
 * @returns {string|null} - The target origin, or null if the host is unknown
 */
export function resolveTargetDomain(hostname) {
  const resolved = resolveHost(hostname);
  return resolved ? DOMAIN_MAP[resolved.environment] || null : null;
}

// The country a config with no `country` column belongs to, and the country a
// caller that resolved no host is treated as. Both are US: every rule that
// predates the Canadian handoff is a US rule, and every two-argument
// shouldRedirect caller is US-era code.
const DEFAULT_REDIRECT_COUNTRY = "US";

/**
 * Check whether a config applies to the deployment a request landed on.
 *
 * Both sides default to US, and both defaults earn their place:
 *
 *   a config with no country  - every rule that predates the Canadian handoff
 *                               is a US rule, and a future rule that forgets
 *                               the column gets the conservative answer.
 *   a call with no country    - a two-argument shouldRedirect behaves exactly
 *                               as it does today AND can never reach a non-US
 *                               rule, so the CA allowlist is unreachable from
 *                               any caller that did not resolve a host.
 *
 * @param {Object} config - A redirect configuration
 * @param {string} [country] - The country the request host resolved to, if any
 * @returns {boolean}
 */
function matchesCountry(config, country) {
  return (
    (config.country || DEFAULT_REDIRECT_COUNTRY) ===
    (country || DEFAULT_REDIRECT_COUNTRY)
  );
}

/**
 * Check whether a path-scoped config applies to the current pathname
 * @param {Object} config - A redirect configuration
 * @param {string} [pathname] - The request pathname, context root stripped
 * @returns {boolean}
 */
function matchesPath(config, pathname) {
  if (!config.paths) return true;
  if (typeof pathname !== "string") return false;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return config.paths.some(
    (path) => normalized === path || normalized.endsWith(path)
  );
}

/**
 * Build the query string forwarded to the target URL
 * @param {Object} config - The matched redirect configuration
 * @param {URLSearchParams} params - The incoming URL search parameters
 * @returns {string} - Query string without the leading "?"
 */
function buildSearch(config, params) {
  if (config.preserveQuery) {
    // clone so callers can keep reading their own params after this
    const forwarded = new URLSearchParams(params.toString());
    forwarded.delete("l");
    stripAuthParams(forwarded);
    return forwarded.toString();
  }
  // An absent sc yields an EMPTY search, never "sc=". The US rules list sc in
  // requiredParams so it is always present and valid by the time this runs;
  // this branch only bites on the Canadian rule, where sc is optional and a
  // bare /buynow?l=175448 has to land on /p/175448 with no query at all.
  const sc = params.get("sc");
  return sc ? "sc=" + encodeURIComponent(sc) : "";
}

/**
 * Remove every SSO credential param from a forwarded query, in place.
 *
 * Matched case-insensitively: the risk is a credential leaving the origin, and
 * a legacy caller sending ?Token= must not defeat that. Keys are collected
 * before deleting so the iterator is not invalidated mid-walk, and delete()
 * removes every occurrence of a repeated key.
 *
 * @param {URLSearchParams} forwarded - mutated in place
 */
function stripAuthParams(forwarded) {
  const denied = new Set(SSO_STRIPPED_QUERY_KEYS);
  Array.from(new Set(Array.from(forwarded.keys())))
    .filter((key) => denied.has(key.toLowerCase()))
    .forEach((key) => forwarded.delete(key));
}

/**
 * Check if a URL should redirect based on query parameters
 * Host-free by design, and that invariant is load bearing: the country a
 * request belongs to comes from the host and nowhere else, so resolving it here
 * would need an argument this function must not have. resolveBuynowRedirect is
 * the entry point that pairs the two.
 *
 * @param {URLSearchParams} params - The URL search parameters
 * @param {string} [pathname] - The request pathname, context root stripped.
 *   Required for path-scoped configs to match.
 * @param {string} [country] - The deployment country resolveHost returned for
 *   the request host. Omitted means "US only", which is today's behavior - see
 *   matchesCountry.
 * @returns {Object|null} - Returns {locationId, sc, search} if a redirect
 *   should occur, null otherwise
 */
export function shouldRedirect(params, pathname, country) {
  if (typeof params?.get !== "function") {
    console.warn("shouldRedirect: Invalid params object provided");
    return null;
  }
  const rd = String(params.get("rd")).toLowerCase();

  // Escape hatch: ?rd=false opts a single request out of every redirect rule
  // so a widget or sales-channel URL can be opened against the legacy app
  if (rd === "false") return null;

  // A missing or malformed location fails safely: fall through to the legacy
  // app rather than building a target path out of unvalidated input
  const lParam = params.get("l");
  if (!lParam || !/^\d+$/.test(lParam)) return null;

  // SSO locations never redirect, under any rule. This is the loop guard - see
  // SSO_DENY_LOCATIONS. It sits ahead of every config so no future rule can
  // reintroduce the handoff cycle.
  if (SSO_DENY_LOCATIONS.includes(lParam)) return null;

  // While a kill switch is off, ?rd=true opts a single request back into that
  // rule (production testing of the handoff). Each override adds ONLY its own
  // config; everything already in REDIRECT_CONFIGS runs either way, so the rest
  // of the redirect system is untouched.
  const configs = [
    ...REDIRECT_CONFIGS,
    ...(!WIDGET_REDIRECT_ENABLED && rd === "true"
      ? [WIDGET_REDIRECT_CONFIG]
      : []),
    ...(!CA_REDIRECT_ENABLED && rd === "true" ? [CA_REDIRECT_CONFIG] : []),
  ];

  // Check each redirect configuration group
  for (const config of configs) {
    // Check if this config applies to the deployment the request landed on
    if (!matchesCountry(config, country)) continue;

    // Check if location is in this config group, when the config is location-keyed
    if (config.locations && !config.locations.includes(lParam)) continue;

    // Check if this config is scoped to specific paths
    if (!matchesPath(config, pathname)) continue;

    // Check if all required parameters match
    let allParamsMatch = true;
    for (const [paramName, allowedValues] of Object.entries(
      config.requiredParams
    )) {
      const paramValue = params.get(paramName);
      if (!paramValue || !allowedValues.includes(paramValue)) {
        allParamsMatch = false;
        break;
      }
    }

    // Optional parameters: absent is fine, present must be recognized. An empty
    // value counts as absent, the same way the required loop above treats "" as
    // missing. This is what lets the Canadian rule accept a bare
    // /buynow?l=175448 while still refusing ?l=175448&sc=bogus rather than
    // forwarding unvalidated attribution.
    if (allParamsMatch && config.optionalParams) {
      for (const [paramName, allowedValues] of Object.entries(
        config.optionalParams
      )) {
        const paramValue = params.get(paramName);
        if (paramValue && !allowedValues.includes(paramValue)) {
          allParamsMatch = false;
          break;
        }
      }
    }

    // If all params match, return redirect info
    if (allParamsMatch) {
      return {
        locationId: lParam,
        sc: params.get("sc"),
        search: buildSearch(config, params),
      };
    }
  }

  return null;
}

/**
 * Build the absolute target URL for a redirect
 * @param {string} targetDomain - The laz.io domain for the current environment
 * @param {Object} redirectInfo - The result of shouldRedirect()
 * @returns {string}
 */
export function buildRedirectUrl(targetDomain, redirectInfo) {
  const search = redirectInfo.search ? "?" + redirectInfo.search : "";
  return `${targetDomain}${TARGET_PATH}/${encodeURIComponent(
    redirectInfo.locationId
  )}${search}`;
}

/**
 * Resolve the new-app URL a /buynow request should redirect to.
 *
 * One function rather than a match/build pair, mirroring resolveAirportRedirect:
 * host FIRST, so no caller can build a target URL for a location that never
 * passed the allowlist, and no caller can pick an origin the request host never
 * authorized. shouldRedirect keeps its host-free signature; this is the only
 * place the two are combined.
 *
 * @param {string} [hostname] - req.hostname or window.location.hostname
 * @param {string} [pathname] - req.path or window.location.pathname
 * @param {URLSearchParams} [params] - the incoming search parameters
 * @returns {string|null} - The absolute target URL, or null to fall through
 */
export function resolveBuynowRedirect(hostname, pathname, params) {
  // Host FIRST: an unrecognized or absent Host can never produce a redirect,
  // whatever the query says.
  const resolved = resolveHost(hostname);
  if (!resolved) return null;

  // Gated on .get, the same guard shouldRedirect uses: Express's req.query is a
  // null-prototype object with no .get (docs/express-buynow-redirect-bug.md).
  const isSearchParams = Boolean(params) && typeof params.get === "function";

  // Read exactly the way shouldRedirect reads it: FIRST value, EXACT-CASE key.
  const rd = isSearchParams ? String(params.get("rd")).toLowerCase() : "";

  // ?rd=true opts a single request into the Canadian rule while its kill switch
  // is off, so the handoff can be validated against production. ?rd=false is
  // handled inside shouldRedirect and opts out of everything, under either
  // branch below.
  const caEnabled = CA_REDIRECT_ENABLED || rd === "true";

  if (!caEnabled) {
    // Byte-for-byte the path running in production today: shouldRedirect ->
    // resolveTargetDomain -> buildRedirectUrl, with no country anywhere. Kept
    // as literal code rather than asserted by tests alone, so "the flag off
    // changes nothing" is visible in the source.
    const redirectInfo = shouldRedirect(params, pathname);
    if (!redirectInfo) return null;

    const targetDomain = resolveTargetDomain(hostname);
    if (!targetDomain) return null;

    return buildRedirectUrl(targetDomain, redirectInfo);
  }

  // Country-scoped. A US location id on the CA host - and a CA id on a US host -
  // matches no config and falls through to the legacy app.
  const redirectInfo = shouldRedirect(params, pathname, resolved.country);
  if (!redirectInfo) return null;

  const origin = (NEW_APP_ORIGINS[resolved.country] || {})[
    resolved.environment
  ];
  if (!origin) return null;

  return buildRedirectUrl(origin, redirectInfo);
}

// /<client> and /<client>/ -> the client root on the new app; /<client>/embed
// and /<client>/embed/ -> that SAME root, because laz.io/a/<client>/embed is a
// hard 404. Anything deeper falls through by design: Airport/index.js routes
// /park, /reserve, /rates, /checkout, /confirmation, /lookup, /account and
// /reservations, and a 301 from there would eject a user mid-booking or off a
// confirmation page - the hazard documented for /buynow in client.js.
const AIRPORT_PATHNAME = /^\/([^/]+)(?:\/(?:embed\/?)?)?$/;

/**
 * Resolve the new-app URL an airport client root should redirect to.
 *
 * One function rather than a match/build pair, so no caller can construct a
 * target URL for a client that never passed the allowlist.
 *
 * @param {string} [hostname] - req.hostname or window.location.hostname
 * @param {string} [pathname] - req.path or window.location.pathname
 * @param {URLSearchParams} [params] - the incoming search parameters
 * @returns {string|null} - The absolute target URL, or null to fall through
 */
export function resolveAirportRedirect(hostname, pathname, params) {
  // Host FIRST: an unrecognized or absent Host can never produce a redirect,
  // whatever the path says.
  const resolved = resolveHost(hostname);
  if (!resolved) return null;

  // Both param reads are gated on .get, the same guard shouldRedirect uses.
  // Express's req.query is a null-prototype object with no .get
  // (docs/express-buynow-redirect-bug.md), and an ordinary plain object would
  // inherit a toString() that forwards "[object Object]" as a query param.
  const isSearchParams = Boolean(params) && typeof params.get === "function";

  // The same escape hatch as shouldRedirect, read the same way: FIRST value,
  // EXACT-CASE key.
  if (isSearchParams && String(params.get("rd")).toLowerCase() === "false") {
    return null;
  }

  if (typeof pathname !== "string") return null;
  const match = AIRPORT_PATHNAME.exec(pathname);
  if (!match) return null;

  // Express routing is case-insensitive, so req.path can arrive as /FLYCAE.
  const clientId = match[1].toLowerCase();
  const clients = AIRPORT_REDIRECT_CLIENTS[resolved.country] || [];
  if (clients.indexOf(clientId) === -1) return null;

  const origin = NEW_APP_ORIGINS[resolved.country][resolved.environment];
  if (!origin) return null;

  // Forward the query so utm_*/gclid attribution survives the hop, minus the
  // SSO credentials: after a 301 those would also sit in a Location RESPONSE
  // header - ALB access logs, intermediary proxies, Referer chains.
  const forwarded = new URLSearchParams(
    isSearchParams ? params.toString() : ""
  );
  stripAuthParams(forwarded);
  const search = forwarded.toString();

  // clientId comes from our own allowlist, so nothing caller-controlled reaches
  // the Location header. No trailing slash: the new app 308s /a/<c>/ -> /a/<c>,
  // so emitting one would make every redirect a 301 -> 308 chain to the same
  // page.
  return (
    `${origin}${AIRPORT_TARGET_PATH}/${encodeURIComponent(clientId)}` +
    (search ? "?" + search : "")
  );
}

// Backward compatibility exports
export const ALLOWED_LOCATIONS = [
  ...LEGACY_REDIRECT_CONFIG.locations,
  ...NEW_REDIRECT_CONFIG.locations,
];
export const ALLOWED_SOURCES = ["t2p", "pnqr"];

